import { fraunces } from '../../Landing/fonts';
import {
  Accent,
  DisplayHeading,
  Eyebrow,
  INK,
  MUTED,
  PAPER_CARD,
  Reveal,
  Stamp,
} from '../../Landing/ui';

const FEATURES = [
  {
    title: 'Your photos are the source',
    text: 'It reads the timestamps and GPS on your own shots to rebuild the trip in order, writes about the places you actually stood in, and drops each photo where the text has earned it.',
    icon: (
      // Camera
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="tw-h-5 tw-w-5" aria-hidden="true">
        <path d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2.2l1.2-2h8.2l1.2 2h2.2A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5z" />
        <circle cx="12" cy="12.5" r="3.2" />
      </svg>
    ),
  },
  {
    title: 'An interview, not a prompt box',
    text: 'Nine questions about what the photos can’t show: prices, mistakes, the people you met, what you’d tell someone to skip. Paste a trip diary and it answers most of them for you.',
    icon: (
      // Speech bubble with question
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="tw-h-5 tw-w-5" aria-hidden="true">
        <path d="M20 4.75H4a1.25 1.25 0 0 0-1.25 1.25v9.5A1.25 1.25 0 0 0 4 16.75h3.5V21l4.2-4.25H20A1.25 1.25 0 0 0 21.25 15.5V6A1.25 1.25 0 0 0 20 4.75z" />
        <path d="M10.4 8.9a1.7 1.7 0 1 1 1.9 2.5v1" />
        <path d="M12.25 13.9h.01" />
      </svg>
    ),
  },
  {
    title: 'It admits what it doesn’t know',
    text: 'A missing opening time comes back as a flag in your draft, not an invented one. Anything it checks on the web arrives as a proposal with a source and a date, and you accept or reject each one.',
    icon: (
      // Flag
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="tw-h-5 tw-w-5" aria-hidden="true">
        <path d="M5 21V4" />
        <path d="M5 5.2c4.2-2 8.8 2 13-.1v8.6c-4.2 2.1-8.8-1.9-13 .1z" />
      </svg>
    ),
  },
  {
    title: 'Structured to rank',
    text: 'Headings that match what people actually search, a table of contents, an FAQ written for the answer box, galleries, internal links. The same shape the posts on page one have.',
    icon: (
      // Magnifier with spark
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="tw-h-5 tw-w-5" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <path d="m21 21-4.35-4.35" />
        <path d="M11 8v6M8 11h6" />
      </svg>
    ),
  },
];

export default function AiKit() {
  return (
    <section id="kit" className="tw-scroll-mt-20 tw-pb-20 tw-pt-4 sm:tw-pb-28">
      <div className="tw-mx-auto tw-max-w-7xl tw-px-4 sm:tw-px-6 lg:tw-px-8">
        <Reveal className="tw-max-w-2xl">
          <Eyebrow>Packed in your kit</Eyebrow>
          <DisplayHeading className="tw-text-3xl sm:tw-text-4xl lg:tw-text-[2.75rem]">
            Everything the AI writes has a <Accent>source</Accent>.
          </DisplayHeading>
        </Reveal>

        <div className="tw-mt-12 tw-grid tw-grid-cols-1 tw-gap-6 sm:tw-grid-cols-2 lg:tw-grid-cols-4">
          {FEATURES.map((feature, i) => (
            <Reveal key={feature.title} delay={0.08 * i}>
              <div
                className={`${PAPER_CARD} tw-h-full tw-rounded-2xl tw-p-6 tw-shadow-[0_14px_32px_-22px_rgba(29,45,40,0.4)] tw-transition-all tw-duration-300 hover:-tw-translate-y-1 hover:tw-shadow-[0_24px_48px_-22px_rgba(29,45,40,0.5)]`}
              >
                <span className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-xl tw-bg-primary-600/10 tw-text-primary-700 dark:tw-bg-primary-300/20 dark:tw-text-primary-300">
                  {feature.icon}
                </span>
                <h3
                  className={`${fraunces.className} ${INK} tw-mb-0 tw-mt-4 tw-text-lg tw-font-semibold tw-tracking-tight`}
                >
                  {feature.title}
                </h3>
                <p
                  className={`${MUTED} tw-mb-0 tw-mt-2 tw-text-[14.5px] tw-leading-relaxed`}
                >
                  {feature.text}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="tw-mt-6">
          <p
            className={`${MUTED} tw-mb-0 tw-text-center tw-text-sm tw-leading-relaxed`}
          >
            Also in the AI tab: Magic Maps. Paste the places you went and get a
            route map to drop into the post.
          </p>
        </Reveal>

        {/* The promise that matters with AI tools */}
        <Reveal className="tw-mt-10">
          <div className="tw-relative tw-rounded-2xl tw-border tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] [border-width:1.5px] tw-bg-[#FFFDF6] dark:tw-bg-[#15231E] tw-p-7 tw-shadow-[0_24px_50px_-30px_rgba(29,45,40,0.4)] sm:tw-p-9">
            <Stamp
              className="tw-absolute -tw-right-2 -tw-top-6 tw-h-20 tw-w-20 tw-rotate-12 tw-text-amber-600/80 dark:tw-text-amber-500/80 sm:-tw-right-4"
              text="TRAVELFEED · YOUR VOICE · "
            />
            <div className="tw-max-w-2xl">
              <h3
                className={`${fraunces.className} ${INK} tw-m-0 tw-text-2xl tw-font-semibold tw-tracking-tight sm:tw-text-[1.85rem]`}
              >
                This is not a <Accent>&ldquo;write me a post&rdquo;</Accent>{' '}
                button.
              </h3>
              <p
                className={`${MUTED} tw-mb-0 tw-mt-3 tw-text-[15px] tw-leading-relaxed`}
              >
                Give it a destination and nothing else and it refuses. Every
                price, name, distance and opening time in the draft comes from
                your photos, your diary or your answers. It never
                &ldquo;recalls&rdquo; facts about a place, never pads to hit a
                word count, and never turns something you called possible into
                something you did. It matches the rhythm of your two most
                recent posts, so the draft reads like you on a good day rather
                than like a chatbot. And it publishes nothing: the draft sits in
                your editor until you say so.
              </p>
              <p
                className={`${MUTED} tw-mb-0 tw-mt-4 tw-text-[15px] tw-leading-relaxed`}
              >
                That&apos;s the trade. Fifteen minutes of your memory buys a
                post that a search engine has a reason to rank, and a reader has
                a reason to finish.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
